<template>
  <div class="col-md-8">
    <div class="ibox">
      <div class="ibox-title">
        <h1>
          Enter {{ orgName }} Membership Info for {{ profileName }}
        </h1>
      </div>
      <div class="ibox-content">
        <div v-if="mostRecentId !== ''" class="alert alert-info" role="alert">
          <strong>Previous {{ orgName }} info found.</strong> Please verify that the member number, expiration date, and membership type matches
          the {{ orgName }} membership you just purchased or renewed.
        </div>
        <div role="form">
          <div class="form-group">
            <label :for="`${orgType}_id`">{{ orgName }} ID</label>
            <input :data-cy="`${orgType}-id`" :id="`${orgType}_id`" v-model="memberId" type="text" class="form-control font-no-ios-zoom">
          </div>
          <div class="form-group">
            <label for="membership_type">Membership type</label>
            <select :data-cy="`${orgType}-type`" id="membership_type" v-model="membershipType" class="form-control font-no-ios-zoom">
              <option selected disabled>Choose membership type...</option>
              <optgroup :label="`${orgName} Membership`">
                <option v-for="type in availableMembershipTypes" :key="type" :value="type">{{ type }}</option>
              </optgroup>
            </select>
          </div>
          <div class="form-group">
            <label for="expiresOn">Expires on</label>
            <input
              :value="expiresOnFormatted"
              class="form-control"
              id="expiresOn"
              data-cy="expiresOn"
              readonly
              disabled
            >
            <div class="text-gray-500 text-12 m-t-xs">
              {{ orgName }} memberships always expire on August 31st. Make sure to renew your membership before then.
            </div>
          </div>
          <div v-if="photoRequired">
            <label>Photo or screenshot of {{ orgName }} card</label>
            <pintura-photo-uploader
                :access-key="accessKey"
                :endpoint="endpoint"
                :cloudfront-base-url="cloudfrontBaseUrl"
                :existing-attachments="existingAttachments"
                :prefix="`${orgType}_cards/`"
                @photos-changed="photosChanged"
            ></pintura-photo-uploader>
          </div>
          <div class="m-t-lg flex">
            <button class="btn btn-default" @click="$emit('cancel-form')">
              Cancel
            </button>
            <button :data-cy="`${orgType}-submit`"
                    class="btn btn-primary align-self-flex-end m-l-auto"
                    @click="submit"
                    :disabled="loading">
              Save
            </button>
          </div>
          <p class="text-danger m-t-m" v-if="error">
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Spinner from './spinner.vue';
import { errorableMix } from './mixins/errorable_mix';
import PinturaPhotoUploader from './pintura_photo_uploader.vue';
import { photoUploadMix } from './mixins/photo_upload_mix';

export default {
  name: 'insurance-membership-form',
  components: {
    PinturaPhotoUploader,
    Spinner,
  },
  mixins: [errorableMix, photoUploadMix],
  props: {
    wrestlerId: {
      type: Number,
      required: true
    },
    mostRecentId: {
      type: String,
      default: ''
    },
    profileName: {
      type: String,
      default: '',
    },
    config: {
      type: String,
      required: true,
    },
    orgType: {
      type: String,
      required: true,
      validator: function(value) {
        return ['usaw', 'aau'].includes(value)
      }
    },
    orgName: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      loading: false,
      memberId: '',
      membershipType: '',
      membershipTypes: {
        usaw: [
          'Full Athlete',
          'High Performance Athlete',
          'Intramural',
          'Limited Folkstyle',
          'Open License'
        ],
        aau: [
          'Regular',
          'Extended Coverage',
        ]
      }
    }
  },
  computed: {
    photoRequired() {
      return this.config === `${this.orgType}_require_all`;
    },
    expiresOn() {
      const today = moment();
      const currentYear = today.year();
      const augustDeadline = moment(`${currentYear}-08-18`);

      return today.isAfter(augustDeadline)
        ? moment(`${currentYear + 1}-08-31`)
        : moment(`${currentYear}-08-31`);
    },
    expiresOnFormatted() {
      return moment(this.expiresOn).format('MM/DD/YYYY');
    },
    availableMembershipTypes() {
      return this.membershipTypes[this.orgType] || [];
    }
  },
  created() {
    this.memberId = this.mostRecentId;
  },
  methods: {
    validate() {
      let error = false;
      let errorMessage = 'Please ensure ';

      if (this.memberId.length === 0) {
        errorMessage += `${this.orgName} ID, `;
        error = true;
      }

      if (this.membershipType.length === 0) {
        errorMessage += 'membership type, ';
        error = true;
      }

      if (this.photoRequired && !this.hasUploadedPhoto) {
        errorMessage += `screenshot of ${this.orgName} card, `;
        error = true;
      }

      if (error) {
        errorMessage = errorMessage.slice(0, -2);
        errorMessage += ' is not empty.';
        this.errorMessage = errorMessage;
      }

      return error;
    },
    submit() {
      const vm = this;
      if (vm.loading === true) { return; }

      vm.error = this.validate();
      if (vm.error) { return; }

      vm.loading = true;
      const params = {
        [`${vm.orgType}_membership`]: {
          public_id: vm.memberId,
          expires_on: vm.expiresOn.toISOString(),
          membership_type: vm.membershipType
        }
      };

      if (vm.photoRequired) {
        params[`${vm.orgType}_membership`].id_card_attributes = vm.hasOneAttachmentParams;
      }

      const url = vm.$apiService[`${vm.orgType}Memberships`](vm.wrestlerId);
      axios.post(url, params)
          .then((response) => {
            vm.loading = false;
            vm.$emit('membership-created', response.data);
          })
          .catch((error) => {
            vm.loading = false;
            vm.errorMessage = `Error creating ${vm.orgName} membership, please try again later or contact support@wrestlingiq.com`;
            vm.error = true;
          });
    }
  }
};
</script>
